import React, {
  FC,
  useEffect,
  useState,
  useCallback,
  useRef,
  HTMLAttributes,
  lazy,
  Suspense,
} from 'react';
import _ from 'lodash';
import { animateScroll } from 'react-scroll';
import { useErrorMonitor, useExperiments } from '@wix/fe-essentials-standalone';
import {
  marketplaceBriefGeneralAction,
  partnersProfilePagePageLoaded,
  copyOfExploreResultsPageGenericClick,
} from '@wix/bi-logger-marketplace/v2';
import { Box } from '@wix/design-system';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntersect } from '@wix/experts-common';
import { useBi } from '@components/Hooks/useBi';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import Header from '@components/Header';
import CompactBrief from '@components/CompactBrief';
import ErrorFallback from '@components/ErrorFallback';
import ContactPanel from './ContactPanel';
import MiniContactPanel from './MiniContactPanel';
import About from './About';
import Services from './Services';
import FeaturedProjects from './FeaturedProjects';
import StartingRates from './StartingRates';
import ProviderLanguages from './ProviderLanguages';
import ProviderLocation from './ProviderLocation';
import ProviderSocialLinksMobile from './ProviderSocialLinksMobile';
import ProjectsReviews from './ProjectsReviews';
import TraceableTraderData from './TraceableTraderData';
import s from './Profile.module.scss';
import {
  ENABLE_PAGINATED_REVIEWS_AND_TEMPLATES,
  ENABLE_SERVICES_AND_REVIEWS_TAB,
  SHOW_MARKETPLACE_PROFILE_CERTIFICATIONS,
  SHOW_MARKETPLACE_PROFILE_SHARE,
} from '@utils/constants';
import CopyNotification from 'components/Profile/CopyNotification';
import { useCopyToClipboard } from '@components/Hooks/useCopyToClipboard';
import ProjectsReviewsWithPaging from '@components/Profile/ProjectsReviews/ProjectsReviewsWithPaging';
import ContactStatusToast from '@components/Profile/ContactStatusToast';
import NavBar from './NavBar';
import { Sections } from './NavBar/NavBar';

const Certifications = lazy(() =>
  import('./Certifications').then((module) => ({
    default: module.default,
  })),
);

interface SectionWrapperProps extends HTMLAttributes<HTMLElement> {
  sectionId: string;
  children: any;
}

const SectionWrapper = React.forwardRef(
  (props: SectionWrapperProps, ref: any) => {
    const { sectionId, children } = props;
    return (
      <section
        id={sectionId}
        tabIndex={-1}
        ref={(el) => {
          if (ref?.current) {
            ref.current[sectionId] = el;
          }
        }}
        className={s.navigationSection}
        {...props}
      >
        {children}
      </section>
    );
  },
);

const Profile: FC = () => {
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { userDetails } = useHeaderContext();
  const {
    partnerProfile,
    entry,
    origin,
    appStatus,
    enabled,
    isRenderedInModal,
    isMobileUserAgent,
  } = useProfileContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const errorMonitor = useErrorMonitor();
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [ref, entryElement] = useIntersect({}) as any;
  const [showShadow, setShowShadow] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [briefTimedOutMessage, setBriefTimedOutMessage] = useState(false);
  const [copyStatus, setCopyStatus] = useState<null | 'success' | 'error'>(
    null,
  );
  const { experiments } = useExperiments({ readOnly: true });
  const isShowingCertifications = experiments.enabled(
    SHOW_MARKETPLACE_PROFILE_CERTIFICATIONS,
  );
  const isShowingShare = experiments.enabled(SHOW_MARKETPLACE_PROFILE_SHARE);
  const isPaginatedReviewsAndTemplatesEnabled = experiments.enabled(
    ENABLE_PAGINATED_REVIEWS_AND_TEMPLATES,
  );
  const isEnabledCombinedServicesAndReviewsTab = experiments.enabled(
    ENABLE_SERVICES_AND_REVIEWS_TAB,
  );
  const targetServicesSectionName = isEnabledCombinedServicesAndReviewsTab
    ? Sections.SERVICES_AND_REVIEWS
    : Sections.SERVICES_AND_RATES;

  const targetReviewsSectionName = isEnabledCombinedServicesAndReviewsTab
    ? Sections.SERVICES_AND_REVIEWS
    : Sections.REVIEWS;

  const { copy } = useCopyToClipboard({
    onSuccess: () => setCopyStatus('success'),
    onError: () => setCopyStatus('error'),
    onClear: () => setCopyStatus(null),
  });

  const navigationRefs = useRef<any>({});

  useEffect(() => {
    if (entryElement.intersectionRatio < 1) {
      setShowShadow(true);
    } else {
      setShowShadow(false);
    }
  }, [entryElement]);

  const handleHashChange = useCallback(() => {
    if (_.includes(window.location.hash, '#brief')) {
      setIsOpen(enabled);
    } else {
      setIsOpen(false);
    }
  }, [userDetails, enabled]);

  useEffect(() => {
    window.addEventListener('popstate', handleHashChange);
    if (_.includes(window.location.hash, '#brief') && enabled) {
      handleHashChange();
    }
    return () => {
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [handleHashChange]);

  function closeModal(exitType?: string) {
    if (exitType !== 'submit') {
      logger.report(
        marketplaceBriefGeneralAction({
          briefType: 'compact',
          action_type: 'exit/cancel',
          sessionId,
          origin,
          entry,
        }),
      );
    }
    history.replaceState(history.state, '', '#');
    setIsOpen(false);
    setBriefTimedOutMessage(false);
  }

  useEffect(() => {
    try {
      if (location.hash) {
        const sectionId = location.hash.slice(1);
        if (document && document.getElementById(sectionId)) {
          const offset =
            document.getElementById(sectionId)!.getBoundingClientRect().top +
            105;
          animateScroll.scrollMore(offset, {
            duration: 1500,
            delay: 100,
            smooth: true,
          });
        }
      }
    } catch (err) {
      errorMonitor.captureException(err as Error);
    }
  }, [errorMonitor]);

  function onContactClicked() {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'connect',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: '',
      }),
    );
    history.replaceState(history.state, '', '#brief');
    handleHashChange();
  }

  function headerOnClick() {
    logger.report(
      copyOfExploreResultsPageGenericClick({
        clickType: 'marketplace top button',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: '',
      }),
    );
  }

  function scrollToReviewsSection() {
    if (navigationRefs.current) {
      const offset = isMobileView ? 130 : 100;
      const targetPosition =
        navigationRefs.current[targetReviewsSectionName].getBoundingClientRect()
          .top + window.scrollY;
      window.scrollTo({
        top: targetPosition - offset,
        behavior: 'smooth',
      });
    }
  }

  useEffect(() => {
    if (appStatus === 'initialized' && !isFirstLoad) {
      logger.report(
        partnersProfilePagePageLoaded({
          sessionId,
          origin: origin || document.referrer,
          entry,
          exploreVersion: 'V2',
          provider_id: partnerProfile.id,
        }),
      );
      setIsFirstLoad(true);
    }
  }, [appStatus, entry, isFirstLoad, origin, partnerProfile.id, sessionId]);

  const contactStatus = showSuccessNotification
    ? 'success'
    : showErrorNotification
    ? 'error'
    : null;

  const clearStatusMap: Record<'success' | 'error', () => void> = {
    success: () => setShowSuccessNotification(false),
    error: () => setShowErrorNotification(false),
  };

  if (isMobileView) {
    return (
      <Box display="block" dataHook="mobile-profile-page">
        {!isRenderedInModal ? (
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        ) : null}
        <ContactStatusToast
          status={contactStatus}
          clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
          isMobileView={isMobileView}
        />
        {isShowingShare && (
          <CopyNotification
            status={copyStatus}
            clearStatus={() => setCopyStatus(null)}
            isMobileView={true}
          />
        )}
        <ContactPanel
          isMobileView={true}
          onContactClicked={onContactClicked}
          copy={copy}
        />
        <div
          ref={ref}
          className={
            showShadow ? `${s.stickyHeader} ${s.showShadow}` : s.stickyHeader
          }
        >
          <MiniContactPanel onContactClicked={onContactClicked} />
          <NavBar isMobileView={true} navigationRefs={navigationRefs} />
        </div>
        <Box marginLeft="24px" marginRight="24px" direction="vertical">
          <SectionWrapper sectionId={Sections.ABOUT} ref={navigationRefs}>
            <About isMobileView={true} />
            <Box paddingTop="24px" direction="vertical">
              <ProviderLocation showTitle={true} marginTop="18px" isMobile />
              <Box height="10px"></Box>
              <ProviderLanguages isMobile />
            </Box>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Services isMobile={isMobileView} />
            </ErrorBoundary>
            <Box>
              <ProviderSocialLinksMobile />
            </Box>
          </SectionWrapper>
          <SectionWrapper
            sectionId={Sections.FEATURED_PROJECTS}
            ref={navigationRefs}
          >
            <FeaturedProjects isMobileView={true} />
          </SectionWrapper>
          <SectionWrapper
            sectionId={targetServicesSectionName}
            ref={navigationRefs}
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <StartingRates isMobileView={true} />
            </ErrorBoundary>
          </SectionWrapper>
          <SectionWrapper
            sectionId={targetReviewsSectionName}
            ref={navigationRefs}
          >
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              {isPaginatedReviewsAndTemplatesEnabled ? (
                <ProjectsReviewsWithPaging
                  isMobileView={isMobileView}
                  onPageChange={scrollToReviewsSection}
                />
              ) : (
                <ProjectsReviews isMobileView={isMobileView} />
              )}
            </ErrorBoundary>
          </SectionWrapper>
        </Box>
        <CompactBrief
          isMobileView={true}
          closeModal={closeModal}
          isOpen={isOpen}
          setShowSuccessNotification={setShowSuccessNotification}
          setShowErrorNotification={setShowErrorNotification}
          setBriefTimedOutMessage={setBriefTimedOutMessage}
          briefTimedOutMessage={briefTimedOutMessage}
        />
      </Box>
    );
  }

  return (
    <Box display="block" dataHook="profile-page" backgroundColor="white">
      {!isRenderedInModal ? (
        <div className={s.stickyHeader}>
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        </div>
      ) : (
        <div className={s.stickyHeader}></div>
      )}
      {isShowingShare && (
        <CopyNotification
          status={copyStatus}
          clearStatus={() => setCopyStatus(null)}
          isMobileView={false}
        />
      )}
      <ContactStatusToast
        status={contactStatus}
        clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
        isMobileView={isMobileView}
      />
      <Box>
        <ContactPanel
          isMobileView={false}
          onContactClicked={onContactClicked}
        />
        <Box direction="vertical" width="calc(100% - 302px)">
          <div>
            <NavBar
              isMobileView={false}
              navigationRefs={navigationRefs}
              copy={copy}
            />
            <Box paddingLeft="96px" paddingRight="96px" maxWidth="1154px">
              <div style={{ width: '100%' }}>
                <SectionWrapper
                  ref={navigationRefs}
                  sectionId={Sections.ABOUT}
                  max-width="770px"
                >
                  <Box direction="horizontal">
                    <Box direction="vertical">
                      <About />
                      <ErrorBoundary FallbackComponent={ErrorFallback}>
                        <Services />
                      </ErrorBoundary>
                    </Box>
                    {isShowingCertifications && (
                      <Suspense fallback="">
                        <Certifications />
                      </Suspense>
                    )}
                  </Box>
                </SectionWrapper>

                <SectionWrapper
                  sectionId={Sections.FEATURED_PROJECTS}
                  ref={navigationRefs}
                >
                  <FeaturedProjects isMobileView={false} />
                </SectionWrapper>
                <SectionWrapper
                  sectionId={targetServicesSectionName}
                  ref={navigationRefs}
                >
                  <Box
                    paddingBottom={
                      isEnabledCombinedServicesAndReviewsTab ? '35px' : '0'
                    }
                  >
                    <StartingRates />
                  </Box>
                  {isEnabledCombinedServicesAndReviewsTab && (
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      {isPaginatedReviewsAndTemplatesEnabled ? (
                        <ProjectsReviewsWithPaging
                          isMobileView={isMobileView}
                          onPageChange={scrollToReviewsSection}
                        />
                      ) : (
                        <ProjectsReviews isMobileView={isMobileView} />
                      )}
                    </ErrorBoundary>
                  )}
                </SectionWrapper>
                {!isEnabledCombinedServicesAndReviewsTab && (
                  <SectionWrapper
                    sectionId={targetReviewsSectionName}
                    ref={navigationRefs}
                  >
                    <ErrorBoundary FallbackComponent={ErrorFallback}>
                      {isPaginatedReviewsAndTemplatesEnabled ? (
                        <ProjectsReviewsWithPaging
                          isMobileView={isMobileView}
                          onPageChange={scrollToReviewsSection}
                        />
                      ) : (
                        <ProjectsReviews isMobileView={isMobileView} />
                      )}
                    </ErrorBoundary>
                  </SectionWrapper>
                )}
                <TraceableTraderData />
              </div>
            </Box>
          </div>
        </Box>
      </Box>
      <CompactBrief
        isMobileView={false}
        closeModal={closeModal}
        isOpen={isOpen}
        setShowSuccessNotification={setShowSuccessNotification}
        setShowErrorNotification={setShowErrorNotification}
        setBriefTimedOutMessage={setBriefTimedOutMessage}
        briefTimedOutMessage={briefTimedOutMessage}
      />
    </Box>
  );
};

export default Profile;
